import { COMPANY, PLATFORM_SUMMARY } from './platformCopy';
import type { ServiceDefinition } from './serviceCatalog';

const BASE_URL = 'https://fast.repair';
const LOGO_URL = 'https://fast.repair/fast-repair-app-logo-v2.png';
const EMAIL = 'support@fast.repair';
const ORGANIZATION_ID = `${BASE_URL}/#organization`;

const HQ_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: 'Preston Park House, South Road',
  addressLocality: 'Brighton',
  addressRegion: 'England',
  postalCode: 'BN1 6SB',
  addressCountry: 'GB',
};

/**
 * Safely serialize a schema object for use in a <script type="application/ld+json"> tag.
 * Escapes '<' to prevent </script> injection when the JSON is placed inside a script element.
 */
export function serializeSchema(schema: object): string {
  return JSON.stringify(schema).replace(/</g, '\\u003c');
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineBusiness',
    '@id': ORGANIZATION_ID,
    name: COMPANY.brand,
    legalName: COMPANY.legalName,
    description: PLATFORM_SUMMARY,
    url: BASE_URL,
    vatID: `GB${COMPANY.vatNumber}`,
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'Companies House company number',
      value: COMPANY.companyNumber,
    },
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
    email: EMAIL,
    address: HQ_ADDRESS,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: EMAIL,
      availableLanguage: 'English',
    },
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    name: 'Fast Repair',
    url: BASE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildServiceSchema(
  service: Pick<ServiceDefinition, 'displayName' | 'category'>,
  serviceSlug: string,
  locationName?: string,
  locationSlug?: string,
) {
  const hasLocation = Boolean(locationName && locationName !== 'Your Area');
  const pageSlug = locationSlug ? `${serviceSlug}-in-${locationSlug}` : serviceSlug;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: hasLocation
      ? `${service.displayName} Repairs in ${locationName}`
      : `${service.displayName} Repairs`,
    serviceType: `${service.displayName} repair`,
    category: service.category,
    description:
      `Fast Repair uses availability-first matching to find the independent local ${service.displayName.toLowerCase()} repair partner who can attend soonest, based on postcode coverage and real-time availability kept up to date by partners in the Fast Repair app. ` +
      'Repairs are carried out by the independent partner business, not by Fast Repair.',
    broker: {
      '@type': 'OnlineBusiness',
      '@id': ORGANIZATION_ID,
      name: COMPANY.brand,
      url: BASE_URL,
    },
    areaServed: hasLocation ? locationName : 'United Kingdom',
    url: `${BASE_URL}/${pageSlug}`,
  };
}

export function buildBreadcrumbSchema(
  serviceName: string,
  serviceSlug: string,
  locationName?: string,
  locationSlug?: string,
) {
  const items: Array<{ '@type': string; position: number; name: string; item: string }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: BASE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: `${serviceName} Repairs`,
      item: `${BASE_URL}/${serviceSlug}`,
    },
  ];

  if (locationName && locationName !== 'Your Area' && locationSlug) {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: `${serviceName} Repairs in ${locationName}`,
      item: `${BASE_URL}/${serviceSlug}-in-${locationSlug}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}
